/**
 * Admin Risk & Compliance Dashboard  v2
 * ──────────────────────────────────────
 * Tabs: Overview · Cases · Users · Events · Policy · Incidents · Audit · Taxonomy
 *
 * 5-tier system: Green · Yellow-Low · Yellow-High · Red · Critical
 * Weighted reason codes, SLA tracking, case decisions, evidence, audit log.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  getRiskDashboard, getRiskCases, getRiskUsers, getRiskEvents,
  getRiskCaseDetail, resolveCase, assignCase, escalateCase, addCaseEvidence,
  rescoreUser, freezeUser, unfreezeUser,
  getActivePolicy, getRiskIncidents, createIncident, resolveIncident,
  getAuditLog, getReasonCodeTaxonomy,
} from '../services/riskApi';
import { Card } from '../components/new-components/ui/card';
import { Button } from '../components/new-components/ui/button';
import {
  ShieldCheck, ShieldAlert, ShieldX, AlertTriangle, Clock, Users,
  FileText, RefreshCw, ChevronRight, X, Loader2, Activity,
  Lock, Unlock, Search, Filter, Eye, BookOpen, List,
} from 'lucide-react';

/* ── helpers ─────────────────────────────────────────── */
const fmt = (n: number) => n?.toLocaleString() ?? '0';
const fmtDate = (d: string) => d ? new Date(d).toLocaleString() : '—';

const TIER_COLORS: Record<string, string> = {
  green:       'bg-emerald-100 text-emerald-800',
  yellow_low:  'bg-yellow-100 text-yellow-800',
  yellow_high: 'bg-amber-100 text-amber-800',
  red:         'bg-red-100 text-red-800',
  critical:    'bg-purple-100 text-purple-900',
};
const TIER_LABELS: Record<string, string> = {
  green: 'Green', yellow_low: 'Yellow-Lo', yellow_high: 'Yellow-Hi', red: 'Red', critical: 'Critical',
};
const STATUS_COLORS: Record<string, string> = {
  open: 'bg-red-100 text-red-800',
  in_review: 'bg-amber-100 text-amber-800',
  waiting_on_user: 'bg-blue-100 text-blue-800',
  closed: 'bg-emerald-100 text-emerald-800',
};
const PRIORITY_COLORS: Record<string, string> = {
  P1: 'bg-red-100 text-red-800',
  P2: 'bg-amber-100 text-amber-800',
  P3: 'bg-slate-100 text-slate-600',
};

const TierBadge = ({ tier }: { tier: string }) => (
  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold uppercase ${TIER_COLORS[tier] || 'bg-gray-100 text-gray-700'}`}>
    {TIER_LABELS[tier] || tier}
  </span>
);
const StatusBadge = ({ status }: { status: string }) => (
  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-700'}`}>
    {status?.replace(/_/g, ' ')}
  </span>
);
const PriorityBadge = ({ p }: { p: string }) => (
  <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${PRIORITY_COLORS[p] || 'bg-gray-100 text-gray-700'}`}>{p}</span>
);

function ReasonCodePills({ codes }: { codes: any[] }) {
  if (!codes || !codes.length) return <span className="text-muted-foreground text-xs">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {codes.map((rc: any, i: number) => (
        <span key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-slate-100 rounded text-xs" title={rc.label || rc.code}>
          <span className="font-mono">{rc.code}</span>
          {rc.weight != null && <span className="text-muted-foreground">({(rc.weight * 100).toFixed(0)}%)</span>}
        </span>
      ))}
    </div>
  );
}

/* ================================================================
   MAIN
   ================================================================ */
export default function AdminRiskDashboard() {
  const [tab, setTab] = useState<string>('overview');

  const tabs = [
    { id: 'overview',  label: 'Overview',  icon: Activity },
    { id: 'cases',     label: 'Cases',     icon: FileText },
    { id: 'users',     label: 'Users',     icon: Users },
    { id: 'events',    label: 'Events',    icon: ShieldAlert },
    { id: 'policy',    label: 'Policy',    icon: ShieldCheck },
    { id: 'incidents', label: 'Incidents', icon: AlertTriangle },
    { id: 'audit',     label: 'Audit Log', icon: BookOpen },
    { id: 'taxonomy',  label: 'Taxonomy',  icon: List },
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-6 p-4 sm:p-6">
      <div>
        <h1 className="text-2xl font-display font-semibold text-[#242424]">Risk & Compliance</h1>
        <p className="text-sm text-muted-foreground mt-1">5-tier risk scoring, case management, policy governance, and audit trail</p>
      </div>

      <div className="flex gap-1 bg-muted/50 rounded-lg p-1 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${tab === t.id ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
            <t.icon size={15} />{t.label}
          </button>
        ))}
      </div>

      {tab === 'overview'  && <OverviewTab />}
      {tab === 'cases'     && <CasesTab />}
      {tab === 'users'     && <UsersTab />}
      {tab === 'events'    && <EventsTab />}
      {tab === 'policy'    && <PolicyTab />}
      {tab === 'incidents' && <IncidentsTab />}
      {tab === 'audit'     && <AuditTab />}
      {tab === 'taxonomy'  && <TaxonomyTab />}
    </div>
  );
}

/* ================================================================
   OVERVIEW
   ================================================================ */
function OverviewTab() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await getRiskDashboard(); setData(r.data?.data); } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Spinner />;
  if (!data) return <p className="text-muted-foreground text-sm">No data</p>;

  const d = data;
  const totalEvents = Object.values(d.events24h || {}).reduce((s: number, n: any) => s + (n || 0), 0);

  return (
    <div className="space-y-6">
      {/* 5-tier KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard icon={ShieldCheck}  label="Green"      value={fmt(d.usersByTier?.green || 0)}       color="text-emerald-600" />
        <KpiCard icon={ShieldAlert}  label="Yellow-Lo"  value={fmt(d.usersByTier?.yellow_low || 0)}  color="text-yellow-600" />
        <KpiCard icon={ShieldAlert}  label="Yellow-Hi"  value={fmt(d.usersByTier?.yellow_high || 0)} color="text-amber-600" />
        <KpiCard icon={ShieldX}      label="Red"        value={fmt(d.usersByTier?.red || 0)}         color="text-red-600" />
        <KpiCard icon={AlertTriangle} label="Critical"  value={fmt(d.usersByTier?.critical || 0)}    color="text-purple-700" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard icon={Users}          label="Total Scored Users" value={fmt(d.totalUsers)} />
        <KpiCard icon={FileText}       label="Open Cases"        value={fmt(Object.values(d.openCasesByQueue || {}).reduce((s: number, q: any) => s + Object.values(q).reduce((a: number, b: any) => a + b, 0), 0))} />
        <KpiCard icon={Clock}          label="SLA Breaches"      value={fmt(d.slaBreaches)} color={d.slaBreaches > 0 ? 'text-red-600' : undefined} />
        <KpiCard icon={AlertTriangle}  label="Active Incidents"  value={fmt(d.activeIncidents)} color={d.activeIncidents > 0 ? 'text-orange-600' : undefined} />
      </div>

      {/* Events 24h by tier */}
      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-3">Risk Events (Last 24h) — {totalEvents as number} total</h3>
        <div className="flex gap-4 flex-wrap text-sm">
          {(['green', 'yellow_low', 'yellow_high', 'red', 'critical'] as const).map(t => (
            <div key={t} className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${t === 'green' ? 'bg-emerald-500' : t === 'yellow_low' ? 'bg-yellow-400' : t === 'yellow_high' ? 'bg-amber-500' : t === 'red' ? 'bg-red-500' : 'bg-purple-600'}`} />
              {TIER_LABELS[t]}: {d.events24h?.[t] || 0}
            </div>
          ))}
        </div>
      </Card>

      {/* Cases by queue */}
      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-3">Open Cases by Queue</h3>
        {Object.keys(d.openCasesByQueue || {}).length === 0 ? (
          <p className="text-muted-foreground text-sm">No open cases</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(d.openCasesByQueue || {}).map(([queue, statuses]: [string, any]) => (
              <div key={queue} className="border rounded-lg p-3">
                <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">{queue.replace(/_/g, ' ')}</p>
                {Object.entries(statuses).map(([st, ct]: [string, any]) => (
                  <div key={st} className="flex justify-between text-sm"><span>{st.replace(/_/g, ' ')}</span><span className="font-mono">{ct}</span></div>
                ))}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

/* ================================================================
   CASES
   ================================================================ */
function CasesTab() {
  const [cases, setCases] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [queueFilter, setQueueFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [selectedCase, setSelectedCase] = useState<any>(null);
  const [caseDetailLoading, setCaseDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { limit: 100 };
      if (statusFilter) params.status = statusFilter;
      if (queueFilter) params.queue = queueFilter;
      if (priorityFilter) params.priority = priorityFilter;
      const r = await getRiskCases(params);
      setCases(r.data?.data || []);
    } catch {} finally { setLoading(false); }
  }, [statusFilter, queueFilter, priorityFilter]);
  useEffect(() => { load(); }, [load]);

  const openCase = async (id: string) => {
    setCaseDetailLoading(true);
    try { const r = await getRiskCaseDetail(id); setSelectedCase(r.data?.data); } catch {} finally { setCaseDetailLoading(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 items-center flex-wrap">
        <Filter size={16} className="text-muted-foreground" />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="text-sm border rounded px-2 py-1">
          <option value="">All Statuses</option>
          <option value="open">Open</option>
          <option value="in_review">In Review</option>
          <option value="waiting_on_user">Waiting on User</option>
          <option value="closed">Closed</option>
        </select>
        <select value={queueFilter} onChange={e => setQueueFilter(e.target.value)} className="text-sm border rounded px-2 py-1">
          <option value="">All Queues</option>
          <option value="support">Support</option>
          <option value="compliance">Compliance</option>
          <option value="risk_eng">Risk Engineering</option>
        </select>
        <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} className="text-sm border rounded px-2 py-1">
          <option value="">All Priorities</option>
          <option value="P1">P1</option>
          <option value="P2">P2</option>
          <option value="P3">P3</option>
        </select>
        <Button variant="ghost" size="sm" onClick={load}><RefreshCw size={14} /></Button>
      </div>

      {loading ? <Spinner /> : cases.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground text-sm">No cases found</Card>
      ) : (
        <div className="space-y-2">
          {cases.map(c => (
            <Card key={c.id} className="p-4 flex items-center gap-4 cursor-pointer hover:bg-muted/30 transition-colors" onClick={() => openCase(c.id)}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-xs font-mono text-muted-foreground">{c.case_number || c.id.slice(0, 16)}</span>
                  <PriorityBadge p={c.priority} />
                  <StatusBadge status={c.status} />
                  <TierBadge tier={c.tier_at_open || c.risk_tier} />
                  <span className="text-xs text-muted-foreground capitalize">{c.queue?.replace(/_/g,' ')}</span>
                  <span className="text-xs text-muted-foreground">{c.case_type?.replace(/_/g,' ')}</span>
                </div>
                <p className="text-sm">{c.user_email || c.user_id}</p>
                <ReasonCodePills codes={typeof c.reason_codes === 'string' ? JSON.parse(c.reason_codes) : c.reason_codes} />
              </div>
              <div className="text-right text-xs text-muted-foreground shrink-0">
                <p>Score: <span className="font-mono font-semibold">{c.score_at_open ?? c.composite_score}</span></p>
                <p>{fmtDate(c.created_at)}</p>
                {c.sla_due_at && new Date(c.sla_due_at) < new Date() && c.status !== 'closed' && (
                  <p className="text-red-600 font-semibold">SLA BREACHED</p>
                )}
              </div>
              <ChevronRight size={16} className="text-muted-foreground" />
            </Card>
          ))}
        </div>
      )}

      {selectedCase && <CaseDetailModal c={selectedCase} onClose={() => setSelectedCase(null)} onRefresh={() => { load(); openCase(selectedCase.id); }} />}
      {caseDetailLoading && <OverlaySpinner />}
    </div>
  );
}

/* ── Case Detail Modal ───────────────────────────────── */
function CaseDetailModal({ c, onClose, onRefresh }: { c: any; onClose: () => void; onRefresh: () => void }) {
  const [resolveOpen, setResolveOpen] = useState(false);
  const [disposition, setDisposition] = useState('');
  const [rationale, setRationale] = useState('');
  const [decisionAction, setDecisionAction] = useState('approve');
  const [noteText, setNoteText] = useState('');
  const [busy, setBusy] = useState(false);

  const handleResolve = async () => {
    if (!disposition || !rationale) return;
    setBusy(true);
    try { await resolveCase(c.id, { disposition, rationale, decision: decisionAction }); onRefresh(); } catch {} finally { setBusy(false); setResolveOpen(false); }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setBusy(true);
    try { await addCaseEvidence(c.id, { content: noteText, evidenceType: 'note', source: 'internal' }); setNoteText(''); onRefresh(); } catch {} finally { setBusy(false); }
  };

  const handleEscalate = async () => {
    setBusy(true);
    try { await escalateCase(c.id, { queue: 'risk_eng', priority: 'P1' }); onRefresh(); } catch {} finally { setBusy(false); }
  };

  const reasonCodes = typeof c.reason_codes === 'string' ? JSON.parse(c.reason_codes || '[]') : (c.reason_codes || []);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-background rounded-xl shadow-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-5" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-lg font-semibold">{c.case_number || c.id.slice(0, 20)}</h2>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <PriorityBadge p={c.priority} />
              <StatusBadge status={c.status} />
              <TierBadge tier={c.tier_at_open || c.risk_tier} />
              <span className="text-xs text-muted-foreground capitalize">{c.queue?.replace(/_/g,' ')}</span>
              <span className="text-xs text-muted-foreground">{c.case_type?.replace(/_/g,' ')}</span>
            </div>
          </div>
          <button onClick={onClose}><X size={20} /></button>
        </div>

        {/* Subject */}
        <Card className="p-4 space-y-2 text-sm">
          <p><span className="text-muted-foreground">User:</span> {c.user_email} ({c.first_name} {c.last_name})</p>
          <p><span className="text-muted-foreground">Score at Open:</span> <span className="font-mono font-bold">{c.score_at_open ?? c.composite_score}</span></p>
          <div><span className="text-muted-foreground">Reason Codes:</span> <ReasonCodePills codes={reasonCodes} /></div>
          <p><span className="text-muted-foreground">SLA Due:</span> {fmtDate(c.sla_due_at)} {c.sla_due_at && new Date(c.sla_due_at) < new Date() && c.status !== 'closed' && <span className="text-red-600 font-bold ml-1">BREACHED</span>}</p>
          <p><span className="text-muted-foreground">Created:</span> {fmtDate(c.created_at)}</p>
          {c.assigned_to && <p><span className="text-muted-foreground">Assigned:</span> {c.assigned_to}</p>}
          {c.closure_reason && <p><span className="text-muted-foreground">Closure:</span> {c.closure_reason}</p>}
          {c.resolution_note && <p><span className="text-muted-foreground">Resolution:</span> {c.resolution_note}</p>}
          {c.tags?.length > 0 && <p><span className="text-muted-foreground">Tags:</span> {c.tags.join(', ')}</p>}
        </Card>

        {/* Trigger Event */}
        {c.triggerEvent && (
          <Card className="p-4">
            <h3 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Trigger Event</h3>
            <div className="text-sm space-y-1">
              <p>Kind: {c.triggerEvent.event_kind} | Subject: {c.triggerEvent.subject_type}/{c.triggerEvent.subject_id}</p>
              <p>User: {c.triggerEvent.user_risk} | Txn: {c.triggerEvent.transaction_risk} | Net: {c.triggerEvent.network_risk} | Composite: <span className="font-bold">{c.triggerEvent.composite_risk}</span></p>
              <p>Tier: <TierBadge tier={c.triggerEvent.tier} /> Action: {c.triggerEvent.recommended_action} Confidence: {c.triggerEvent.confidence}</p>
              <ReasonCodePills codes={typeof c.triggerEvent.reason_codes === 'string' ? JSON.parse(c.triggerEvent.reason_codes) : c.triggerEvent.reason_codes} />
            </div>
          </Card>
        )}

        {/* Decisions timeline */}
        <Card className="p-4">
          <h3 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Decision Trail</h3>
          {(c.decisions || []).length === 0 ? <p className="text-sm text-muted-foreground">No decisions</p> : (
            <div className="space-y-2">
              {c.decisions.map((d: any) => (
                <div key={d.id} className="border-l-2 border-muted pl-3 text-sm">
                  <p className="font-medium">{d.decision} {d.disposition_label && `— ${d.disposition_label}`} <span className="text-xs text-muted-foreground">({d.decided_by})</span></p>
                  <p className="text-muted-foreground">{d.rationale}</p>
                  <p className="text-xs text-muted-foreground">{fmtDate(d.created_at)}</p>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Evidence */}
        <Card className="p-4">
          <h3 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Evidence</h3>
          {(c.evidence || []).length === 0 ? <p className="text-sm text-muted-foreground">No evidence</p> : (
            <div className="space-y-2">
              {c.evidence.map((e: any) => {
                const meta = typeof e.metadata === 'string' ? JSON.parse(e.metadata || '{}') : (e.metadata || {});
                return (
                  <div key={e.id} className="border rounded p-2 text-sm">
                    <p className="font-medium">{meta.title || e.evidence_type} <span className="text-xs text-muted-foreground">({e.source})</span></p>
                    {meta.content && <p>{meta.content}</p>}
                    {e.uri && <p className="text-xs text-blue-600 break-all">{e.uri}</p>}
                    <p className="text-xs text-muted-foreground">{fmtDate(e.created_at)} by {e.added_by}</p>
                  </div>
                );
              })}
            </div>
          )}
          {c.status !== 'closed' && (
            <div className="flex gap-2 mt-3">
              <input value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add a note…" className="flex-1 border rounded px-2 py-1 text-sm" />
              <Button size="sm" onClick={handleAddNote} disabled={busy}>Add</Button>
            </div>
          )}
        </Card>

        {/* Actions */}
        {c.status !== 'closed' && (
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={handleEscalate} disabled={busy}>Escalate to P1 / Risk Eng</Button>
            <Button variant="default" size="sm" onClick={() => setResolveOpen(true)} disabled={busy}>Resolve Case</Button>
          </div>
        )}

        {/* Resolve form */}
        {resolveOpen && (
          <Card className="p-4 space-y-3">
            <h3 className="text-sm font-semibold">Resolve Case</h3>
            <select value={decisionAction} onChange={e => setDecisionAction(e.target.value)} className="w-full border rounded px-2 py-1 text-sm">
              <option value="approve">Approve (release)</option>
              <option value="deny">Deny (block)</option>
              <option value="hold">Hold (maintain)</option>
              <option value="freeze">Freeze account</option>
              <option value="offboard">Offboard user</option>
              <option value="request_info">Request info</option>
            </select>
            <select value={disposition} onChange={e => setDisposition(e.target.value)} className="w-full border rounded px-2 py-1 text-sm">
              <option value="">Select disposition…</option>
              <option value="fraud">Confirmed Fraud</option>
              <option value="false_positive">False Positive</option>
              <option value="user_error">User Error</option>
              <option value="policy_hold">Policy Hold</option>
              <option value="compliant">Compliant</option>
              <option value="offboarded">Offboarded</option>
              <option value="sanctions_match">Sanctions Match</option>
              <option value="pep_match">PEP Match</option>
              <option value="aml_flag">AML Flag</option>
            </select>
            <textarea value={rationale} onChange={e => setRationale(e.target.value)} placeholder="Rationale (required)" className="w-full border rounded px-2 py-1 text-sm min-h-[60px]" />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleResolve} disabled={busy || !disposition || !rationale}>Submit</Button>
              <Button variant="ghost" size="sm" onClick={() => setResolveOpen(false)}>Cancel</Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

/* ================================================================
   USERS
   ================================================================ */
function UsersTab() {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tierFilter, setTierFilter] = useState('');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { limit: 100 };
      if (tierFilter) params.tier = tierFilter;
      if (search) params.search = search;
      const r = await getRiskUsers(params);
      setUsers(r.data?.data || []);
    } catch {} finally { setLoading(false); }
  }, [tierFilter, search]);
  useEffect(() => { load(); }, [load]);

  const handleRescore = async (userId: string) => { setBusy(true); try { await rescoreUser(userId); load(); } catch {} finally { setBusy(false); } };
  const handleFreeze = async (userId: string) => { setBusy(true); try { await freezeUser(userId, 'Admin action'); load(); } catch {} finally { setBusy(false); } };
  const handleUnfreeze = async (userId: string) => { setBusy(true); try { await unfreezeUser(userId); load(); } catch {} finally { setBusy(false); } };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 items-center flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search email/name…" className="w-full border rounded pl-7 pr-2 py-1 text-sm" />
        </div>
        <select value={tierFilter} onChange={e => setTierFilter(e.target.value)} className="text-sm border rounded px-2 py-1">
          <option value="">All Tiers</option>
          <option value="green">Green</option>
          <option value="yellow_low">Yellow-Low</option>
          <option value="yellow_high">Yellow-High</option>
          <option value="red">Red</option>
          <option value="critical">Critical</option>
        </select>
        <Button variant="ghost" size="sm" onClick={load}><RefreshCw size={14} /></Button>
      </div>

      {loading ? <Spinner /> : users.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground text-sm">No scored users yet</Card>
      ) : (
        <div className="border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-4 py-2 font-medium">User</th>
                <th className="text-center px-2 py-2 font-medium">Score</th>
                <th className="text-center px-2 py-2 font-medium">Tier</th>
                <th className="text-center px-2 py-2 font-medium hidden md:table-cell">Frozen</th>
                <th className="text-left px-2 py-2 font-medium hidden lg:table-cell">Reason Codes</th>
                <th className="text-right px-4 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {users.map(u => (
                <tr key={u.id} className="hover:bg-muted/20">
                  <td className="px-4 py-2">
                    <p className="font-medium">{u.first_name} {u.last_name}</p>
                    <p className="text-xs text-muted-foreground">{u.email}</p>
                  </td>
                  <td className="text-center px-2 py-2 font-mono font-bold">{u.risk_score}</td>
                  <td className="text-center px-2 py-2"><TierBadge tier={u.risk_tier} /></td>
                  <td className="text-center px-2 py-2 hidden md:table-cell">{u.is_frozen ? <Lock size={14} className="text-red-500 mx-auto" /> : '—'}</td>
                  <td className="px-2 py-2 hidden lg:table-cell max-w-[300px]">
                    <ReasonCodePills codes={u.reason_codes_json ? (typeof u.reason_codes_json === 'string' ? JSON.parse(u.reason_codes_json) : u.reason_codes_json) : (u.reason_codes || []).map((c: string) => ({ code: c }))} />
                  </td>
                  <td className="text-right px-4 py-2">
                    <div className="flex gap-1 justify-end">
                      <Button variant="ghost" size="sm" title="Re-score" onClick={() => handleRescore(u.user_id)} disabled={busy}><RefreshCw size={13} /></Button>
                      {u.is_frozen ? (
                        <Button variant="ghost" size="sm" title="Unfreeze" onClick={() => handleUnfreeze(u.user_id)} disabled={busy}><Unlock size={13} /></Button>
                      ) : (
                        <Button variant="ghost" size="sm" title="Freeze" onClick={() => handleFreeze(u.user_id)} disabled={busy}><Lock size={13} /></Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ================================================================
   EVENTS
   ================================================================ */
function EventsTab() {
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tierFilter, setTierFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, limit };
      if (tierFilter) params.tier = tierFilter;
      const r = await getRiskEvents(params);
      setEvents(r.data?.data || []);
      setTotal(r.data?.pagination?.total || 0);
    } catch {} finally { setLoading(false); }
  }, [page, tierFilter]);
  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-4">
      <div className="flex gap-2 items-center">
        <select value={tierFilter} onChange={e => { setTierFilter(e.target.value); setPage(1); }} className="text-sm border rounded px-2 py-1">
          <option value="">All Tiers</option>
          <option value="green">Green</option>
          <option value="yellow_low">Yellow-Low</option>
          <option value="yellow_high">Yellow-High</option>
          <option value="red">Red</option>
          <option value="critical">Critical</option>
        </select>
        <span className="text-xs text-muted-foreground ml-auto">{total} events</span>
      </div>

      {loading ? <Spinner /> : events.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground text-sm">No risk events</Card>
      ) : (
        <div className="border rounded-lg overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Time</th>
                <th className="text-left px-3 py-2 font-medium">User</th>
                <th className="text-left px-3 py-2 font-medium">Event</th>
                <th className="text-center px-2 py-2 font-medium">U</th>
                <th className="text-center px-2 py-2 font-medium">T</th>
                <th className="text-center px-2 py-2 font-medium">N</th>
                <th className="text-center px-2 py-2 font-medium">C</th>
                <th className="text-center px-2 py-2 font-medium">Tier</th>
                <th className="text-left px-2 py-2 font-medium">Action</th>
                <th className="text-left px-2 py-2 font-medium hidden lg:table-cell">Reason Codes</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {events.map(e => (
                <tr key={e.id} className="hover:bg-muted/20">
                  <td className="px-3 py-2 text-xs text-muted-foreground">{fmtDate(e.created_at)}</td>
                  <td className="px-3 py-2">{e.user_email || (e.user_id && e.user_id.slice(0, 8))}</td>
                  <td className="px-3 py-2 text-xs">{e.event_kind}</td>
                  <td className="text-center px-2 py-2 font-mono text-xs">{e.user_risk}</td>
                  <td className="text-center px-2 py-2 font-mono text-xs">{e.transaction_risk}</td>
                  <td className="text-center px-2 py-2 font-mono text-xs">{e.network_risk}</td>
                  <td className="text-center px-2 py-2 font-mono font-bold">{e.composite_risk}</td>
                  <td className="text-center px-2 py-2"><TierBadge tier={e.tier} /></td>
                  <td className="px-2 py-2 text-xs">{e.recommended_action}</td>
                  <td className="px-2 py-2 hidden lg:table-cell max-w-[250px]">
                    <ReasonCodePills codes={typeof e.reason_codes === 'string' ? JSON.parse(e.reason_codes) : e.reason_codes} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex gap-2 justify-center items-center">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
          <span className="text-sm text-muted-foreground">{page} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}

/* ================================================================
   POLICY
   ================================================================ */
function PolicyTab() {
  const [policy, setPolicy] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { const r = await getActivePolicy(); setPolicy(r.data?.data); } catch {} finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <Spinner />;
  if (!policy) return <Card className="p-10 text-center text-muted-foreground text-sm">No active policy</Card>;

  return (
    <div className="space-y-4">
      <Card className="p-5 space-y-3">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="font-semibold">{policy.name} <span className="text-xs font-mono text-muted-foreground">v{policy.version}</span></h3>
            <p className="text-sm text-muted-foreground">{policy.description}</p>
          </div>
          <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 text-xs font-semibold rounded-full">ACTIVE</span>
        </div>

        {/* 5-tier thresholds */}
        <div>
          <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Tier Thresholds</h4>
          <div className="grid grid-cols-5 gap-2 text-sm">
            <div className="border rounded p-2 bg-emerald-50"><p className="text-xs text-muted-foreground">Green</p><p className="font-mono font-bold">0 – {policy.green_max}</p></div>
            <div className="border rounded p-2 bg-yellow-50"><p className="text-xs text-muted-foreground">Yellow-Lo</p><p className="font-mono font-bold">{(policy.green_max || 39) + 1} – {policy.yellow_low_max || 54}</p></div>
            <div className="border rounded p-2 bg-amber-50"><p className="text-xs text-muted-foreground">Yellow-Hi</p><p className="font-mono font-bold">{(policy.yellow_low_max || 54) + 1} – {policy.yellow_high_max || 69}</p></div>
            <div className="border rounded p-2 bg-red-50"><p className="text-xs text-muted-foreground">Red</p><p className="font-mono font-bold">{(policy.yellow_high_max || 69) + 1} – {policy.red_max || 84}</p></div>
            <div className="border rounded p-2 bg-purple-50"><p className="text-xs text-muted-foreground">Critical</p><p className="font-mono font-bold">{(policy.red_max || 84) + 1} – 100</p></div>
          </div>
        </div>

        {/* SLA */}
        <div>
          <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">SLA Hours</h4>
          <div className="grid grid-cols-4 gap-2 text-sm">
            <div className="border rounded p-2"><p className="text-xs text-muted-foreground">Yellow-Lo</p><p className="font-mono">{policy.yellow_sla_hours || 24}h</p></div>
            <div className="border rounded p-2"><p className="text-xs text-muted-foreground">Yellow-Hi</p><p className="font-mono">{policy.yellow_high_sla_hours || 8}h</p></div>
            <div className="border rounded p-2"><p className="text-xs text-muted-foreground">Red</p><p className="font-mono">{policy.red_sla_hours || 4}h</p></div>
            <div className="border rounded p-2"><p className="text-xs text-muted-foreground">Critical</p><p className="font-mono">{policy.critical_sla_hours || 1}h</p></div>
          </div>
        </div>

        {/* Weights */}
        <div>
          <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Composite Weights</h4>
          <div className="grid grid-cols-4 gap-2 text-sm">
            <div className="border rounded p-2"><p className="text-xs text-muted-foreground">W(User)</p><p className="font-mono">{policy.w_user}</p></div>
            <div className="border rounded p-2"><p className="text-xs text-muted-foreground">W(Txn)</p><p className="font-mono">{policy.w_txn}</p></div>
            <div className="border rounded p-2"><p className="text-xs text-muted-foreground">W(Network)</p><p className="font-mono">{policy.w_network}</p></div>
            <div className="border rounded p-2"><p className="text-xs text-muted-foreground">W(Context)</p><p className="font-mono">{policy.w_context}</p></div>
          </div>
        </div>
      </Card>

      {/* Control Matrix */}
      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-3">Tier → Control Matrix</h3>
        <div className="border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-3 py-2">Tier</th>
                <th className="text-left px-3 py-2">Score Range</th>
                <th className="text-left px-3 py-2">Default Control</th>
                <th className="text-left px-3 py-2">Case?</th>
                <th className="text-left px-3 py-2">SLA</th>
                <th className="text-left px-3 py-2">Owner</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              <tr className="bg-emerald-50/50"><td className="px-3 py-2"><TierBadge tier="green" /></td><td className="px-3 py-2 font-mono">0–{policy.green_max}</td><td className="px-3 py-2">Allow</td><td className="px-3 py-2">No</td><td className="px-3 py-2">N/A</td><td className="px-3 py-2">System</td></tr>
              <tr className="bg-yellow-50/50"><td className="px-3 py-2"><TierBadge tier="yellow_low" /></td><td className="px-3 py-2 font-mono">{(policy.green_max||39)+1}–{policy.yellow_low_max||54}</td><td className="px-3 py-2">Step-up</td><td className="px-3 py-2">Sometimes</td><td className="px-3 py-2">{policy.yellow_sla_hours||24}h</td><td className="px-3 py-2">Support (P3)</td></tr>
              <tr className="bg-amber-50/50"><td className="px-3 py-2"><TierBadge tier="yellow_high" /></td><td className="px-3 py-2 font-mono">{(policy.yellow_low_max||54)+1}–{policy.yellow_high_max||69}</td><td className="px-3 py-2">Hold + Review</td><td className="px-3 py-2">Yes</td><td className="px-3 py-2">{policy.yellow_high_sla_hours||8}h</td><td className="px-3 py-2">Compliance (P2)</td></tr>
              <tr className="bg-red-50/50"><td className="px-3 py-2"><TierBadge tier="red" /></td><td className="px-3 py-2 font-mono">{(policy.yellow_high_max||69)+1}–{policy.red_max||84}</td><td className="px-3 py-2">Hold + Case</td><td className="px-3 py-2">Yes</td><td className="px-3 py-2">{policy.red_sla_hours||4}h</td><td className="px-3 py-2">Compliance (P1)</td></tr>
              <tr className="bg-purple-50/50"><td className="px-3 py-2"><TierBadge tier="critical" /></td><td className="px-3 py-2 font-mono">{(policy.red_max||84)+1}–100</td><td className="px-3 py-2">Block + Freeze</td><td className="px-3 py-2">Yes</td><td className="px-3 py-2">{policy.critical_sla_hours||1}h</td><td className="px-3 py-2">Risk Lead (P1)</td></tr>
            </tbody>
          </table>
        </div>
      </Card>

      {/* Rules */}
      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-3">Rules ({(policy.rules || []).length})</h3>
        <div className="border rounded-lg overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-3 py-2 font-medium">ID</th>
                <th className="text-left px-3 py-2 font-medium">Name</th>
                <th className="text-left px-3 py-2 font-medium">Condition</th>
                <th className="text-center px-2 py-2 font-medium">Score +</th>
                <th className="text-left px-2 py-2 font-medium">Reason Code</th>
                <th className="text-left px-2 py-2 font-medium">Action</th>
                <th className="text-left px-2 py-2 font-medium">Category</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {(policy.rules || []).map((r: any) => (
                <tr key={r.id} className="hover:bg-muted/20">
                  <td className="px-3 py-2 font-mono text-xs">{r.id}</td>
                  <td className="px-3 py-2">{r.name}</td>
                  <td className="px-3 py-2 font-mono text-xs text-muted-foreground max-w-[200px] truncate">{r.condition}</td>
                  <td className="text-center px-2 py-2 font-mono font-bold text-amber-700">+{r.score_add}</td>
                  <td className="px-2 py-2 text-xs font-mono">{r.reason}</td>
                  <td className="px-2 py-2 text-xs">{r.action || '—'}</td>
                  <td className="px-2 py-2 text-xs text-muted-foreground">{r.category?.replace(/_/g, ' ') || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

/* ================================================================
   INCIDENTS
   ================================================================ */
function IncidentsTab() {
  const [incidents, setIncidents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [boost, setBoost] = useState(20);
  const [freeze, setFreeze] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await getRiskIncidents(); setIncidents(r.data?.data || []); } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try { await createIncident({ name, description: desc, context_boost: boost, freeze_payouts: freeze }); setName(''); setDesc(''); setBoost(20); setFreeze(false); setShowCreate(false); load(); } catch {} finally { setBusy(false); }
  };

  const handleResolve = async (id: string) => {
    setBusy(true);
    try { await resolveIncident(id); load(); } catch {} finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">Incidents boost the context risk score and can globally freeze payouts.</p>
        <Button size="sm" onClick={() => setShowCreate(!showCreate)}>{showCreate ? 'Cancel' : 'New Incident'}</Button>
      </div>

      {showCreate && (
        <Card className="p-4 space-y-3">
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Incident name" className="w-full border rounded px-2 py-1 text-sm" />
          <textarea value={desc} onChange={e => setDesc(e.target.value)} placeholder="Description" className="w-full border rounded px-2 py-1 text-sm min-h-[50px]" />
          <div className="flex gap-4 items-center text-sm">
            <label>Context Boost: <input type="number" value={boost} onChange={e => setBoost(+e.target.value)} className="border rounded w-16 px-1 py-0.5 ml-1" /></label>
            <label className="flex items-center gap-1"><input type="checkbox" checked={freeze} onChange={e => setFreeze(e.target.checked)} /> Freeze Payouts</label>
          </div>
          <Button size="sm" onClick={handleCreate} disabled={busy || !name.trim()}>Activate Incident</Button>
        </Card>
      )}

      {loading ? <Spinner /> : incidents.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground text-sm">No incidents</Card>
      ) : (
        <div className="space-y-2">
          {incidents.map(inc => (
            <Card key={inc.id} className="p-4 flex items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-sm">{inc.name}</p>
                  {inc.is_active ? <span className="bg-red-100 text-red-800 px-2 py-0.5 text-xs font-semibold rounded-full">ACTIVE</span> : <span className="bg-gray-100 text-gray-600 px-2 py-0.5 text-xs rounded-full">Resolved</span>}
                  {inc.freeze_payouts && inc.is_active && <span className="bg-orange-100 text-orange-800 px-2 py-0.5 text-xs rounded-full">PAYOUTS FROZEN</span>}
                </div>
                <p className="text-xs text-muted-foreground mt-1">{inc.description}</p>
                <p className="text-xs text-muted-foreground">Boost: +{inc.context_boost} · Created: {fmtDate(inc.created_at)} {inc.resolved_at && `· Resolved: ${fmtDate(inc.resolved_at)}`}</p>
              </div>
              {inc.is_active && <Button variant="outline" size="sm" onClick={() => handleResolve(inc.id)} disabled={busy}>Resolve</Button>}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ================================================================
   AUDIT LOG
   ================================================================ */
function AuditTab() {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getAuditLog({ page, limit });
      setLogs(r.data?.data || []);
      setTotal(r.data?.pagination?.total || 0);
    } catch {} finally { setLoading(false); }
  }, [page]);
  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Immutable audit trail of all admin actions on risk entities.</p>

      {loading ? <Spinner /> : logs.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground text-sm">No audit entries</Card>
      ) : (
        <div className="border rounded-lg overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Time</th>
                <th className="text-left px-3 py-2 font-medium">Actor</th>
                <th className="text-left px-3 py-2 font-medium">Action</th>
                <th className="text-left px-3 py-2 font-medium">Entity</th>
                <th className="text-left px-3 py-2 font-medium hidden lg:table-cell">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {logs.map(l => (
                <tr key={l.id} className="hover:bg-muted/20">
                  <td className="px-3 py-2 text-xs text-muted-foreground">{fmtDate(l.created_at)}</td>
                  <td className="px-3 py-2 text-xs">{l.actor}</td>
                  <td className="px-3 py-2"><span className="bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded text-xs font-mono">{l.action}</span></td>
                  <td className="px-3 py-2 text-xs">{l.entity_type}/{l.entity_id?.slice(0, 12)}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground hidden lg:table-cell max-w-[300px] truncate">{l.after_state ? JSON.stringify(l.after_state) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex gap-2 justify-center items-center">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
          <span className="text-sm text-muted-foreground">{page} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}

/* ================================================================
   TAXONOMY
   ================================================================ */
function TaxonomyTab() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { const r = await getReasonCodeTaxonomy(); setData(r.data?.data); } catch {} finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <Spinner />;
  if (!data) return <Card className="p-10 text-center text-muted-foreground text-sm">Unable to load taxonomy</Card>;

  const categories = data.categories || {};
  const codes = data.codes || {};

  const grouped: Record<string, { code: string; label: string }[]> = {};
  for (const [code, meta] of Object.entries(codes) as [string, any][]) {
    const cat = meta.category;
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push({ code, label: meta.label });
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Canonical risk reason codes organised by category. Every risk decision returns 1–5 weighted codes.</p>

      {Object.entries(grouped).map(([cat, codes]) => (
        <Card key={cat} className="p-4">
          <h3 className="text-sm font-semibold mb-2">{categories[cat] || cat} <span className="text-xs text-muted-foreground font-normal">({codes.length} codes)</span></h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
            {codes.map(c => (
              <div key={c.code} className="flex items-center gap-2 text-sm py-0.5">
                <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{c.code}</span>
                <span className="text-muted-foreground">{c.label}</span>
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}

/* ── Shared ────────────────────────────────────────── */
function KpiCard({ icon: Icon, label, value, color }: { icon: any; label: string; value: string; color?: string }) {
  return (
    <Card className="p-4 flex items-center gap-3">
      <Icon size={20} className={color || 'text-muted-foreground'} />
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className={`text-xl font-mono font-bold ${color || ''}`}>{value}</p>
      </div>
    </Card>
  );
}

function Spinner() {
  return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-primary" size={28} /></div>;
}

function OverlaySpinner() {
  return <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50"><Loader2 className="animate-spin text-primary" size={36} /></div>;
}
